import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Card,
    CardMedia,
    Checkbox,
    CircularProgress,
    Container,
    FormControlLabel,
    IconButton,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import * as lodash from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as GeometryHelper from '../../api/geometry/geometryHelper';
import { IResponse } from '../../api/httpClient';
import * as MaterialHelper from '../../api/material/materialHelper';
import Input from '../../design/Input';
import { ELanguages, TLanguages, languages } from '../../localization';
import { route } from '../../route/Index';
import InputText from '../../shared/component/InputText';
import { EStatus, EUnitOfMeasure } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { IProject, ITranslation } from '../../shared/iProject';
import variable from '../../shared/style/variable';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { selectProject, setGeometry, setMaterial, setMaterialDoubleLayer, setProjectInfo } from '../../store/project/projectSlice';

interface IGeometryDetailProps {}

const GeometryContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

let translations: ITranslation[] = [];

const GeometryDetail = (props: IGeometryDetailProps) => {
    let { param } = useParams();

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = React.useState(false);
    const [isPublic, setIsPublic] = React.useState(false);
    const [image, setImage] = React.useState('');

    React.useEffect(() => {
        const fetchGeometry = async () => {
            setIsLoading(true);
            let geometryId = 0;
            if (param === 'newGeometry') {
                let res = await GeometryHelper.getGeometryPreviewAsync(1, 1);
                geometryId = res.data.value[0].id;
            } else {
                geometryId = parseInt(param!);
            }
            dispatch(setProjectInfo({ unitOfMeasure: EUnitOfMeasure.Metric }));
            let initialGeometry = await GeometryHelper.getGeometryByIdAsync(geometryId);
            dispatch(setGeometry(initialGeometry.data));
            let initialMaterial = await MaterialHelper.getMaterialByIdAsync(initialGeometry.data.materialId);
            dispatch(setMaterial(initialMaterial.data));
            if (initialGeometry.data.doubleLayerNearToTheQuayWall) {
                let initialMaterialDoubleLayer = await MaterialHelper.getMaterialByIdAsync(initialGeometry.data.doubleLayerMaterialId);
                dispatch(setMaterialDoubleLayer(initialMaterialDoubleLayer.data));
            }
            translations = lodash.cloneDeep(initialGeometry.data.translations);
            setImage(initialGeometry.data.image.imageUrl);
            setIsPublic(initialGeometry.data.status == EStatus.Public);
            setIsLoading(false);
        };
        fetchGeometry();
    }, []);

    const handleClickSave = async () => {
        let payload: IProject = {
            ...project.data,
            geometry: {
                ...project.data!.geometry!,
                status: isPublic ? EStatus.Public : EStatus.Test,
                translations,
            },
        };
        const formData = new FormData();
        formData.append('geometry', JSON.stringify(payload.geometry));
        formData.append('imageFile', image);
        formData.append('material', JSON.stringify(payload.material));
        formData.append('doubleLayerMaterial', JSON.stringify(payload.doubleLayerMaterial));
        let res: IResponse<IProject>;
        if (param === 'newGeometry') {
            res = await GeometryHelper.InsertGeometryAsync(formData);
        } else {
            res = await GeometryHelper.updateGeometryWithMaterialsAsync(formData);
        }
        if (res.status == 200) {
            Swal.fire({
                title: t('savedGeometry'),
                icon: 'success',
                toast: true,
                position: 'top',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };

    const handleChangeTraslation = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let index = translations.findIndex(x => x.iso === name);
        if (index !== -1) {
            translations[index].name = value;
        } else {
            translations.push({
                iso: name,
                name: value,
            });
        }
    };

    const handleClickRemoveTranslation = (iso: string) => (translations = translations.filter(x => x.iso !== iso));

    const handleImageChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        const file = event.target.files[0];
        setImage(file);
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <ArrowForwardIosIcon style={{ fill: variable.greenLight }} />
                <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                    {param == 'newGeometry' ? t('addNewGeometry') : t('updateGeometry')}
                </Typography>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && project.data?.geometry && project.data?.material && (
                <GeometryContainer maxWidth={'md'}>
                    <Stack height={'95%'} direction='column'>
                        <Stack padding={'0 1rem'}>
                            <FormControlLabel control={<Checkbox checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />} label={t('public')} />
                        </Stack>
                        <Stack height={'100%'} direction={'row'} spacing={1}>
                            <Stack width={'33%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {Object.keys(languages).map(x => (
                                        <Accordion disableGutters>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                <Typography>{languages[x as TLanguages]}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack direction={'column'} spacing={2}>
                                                    <InputText
                                                        defaultValue={project.data?.geometry?.translations.find(y => y.iso == x)?.name}
                                                        id={x}
                                                        inputLabel={t('name')}
                                                        name={x}
                                                        customOnChange={handleChangeTraslation}
                                                    />
                                                    {x !== ELanguages.en && (
                                                        <Stack alignItems={'end'}>
                                                            <IconButton aria-label={`delete-geometry-${x}`} onClick={() => handleClickRemoveTranslation(x)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            </Stack>
                            <Stack width={'33%'} position='relative'>
                                <Input />
                            </Stack>
                            <Stack width={'33%'}>
                                <Accordion disableGutters>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                        <Typography>{t('pictures')}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack direction={'column'} spacing={2}>
                                            {image ? (
                                                <>
                                                    <Card variant='standard'>
                                                        <CardMedia height={160} component='img' image={typeof image === 'string' ? image : URL.createObjectURL(image)} alt='image-geometry' />
                                                    </Card>
                                                    <IconButton style={{ justifyContent: 'end' }} aria-label='remove-image-geometry' onClick={() => setImage('')}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <Stack height={160} justifyContent={'center'} style={{ border: `0.1rem solid ${variable.greyLight}` }}>
                                                    <IconButton aria-label='add-image-geometry' onClick={() => fileInputRef.current!.click()}>
                                                        <UploadIcon />
                                                    </IconButton>
                                                    <input onChange={handleImageChange} multiple={false} ref={fileInputRef} type='file' hidden />
                                                </Stack>
                                            )}
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            </Stack>
                        </Stack>
                        <Stack alignItems='end'>
                            <Stack direction={'row'} spacing={2}>
                                <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                    {t('back')}
                                </Button>
                                <Button variant='orange' onClick={handleClickSave}>
                                    {t('save')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </GeometryContainer>
            )}
        </>
    );
};

export default isAdmin(GeometryDetail);
